<template>
  <div class="payments-completed-container">
    <div v-if="isLoading" class="loading-animation-container">
      <div class="animation-container">
        <div class="loading-animation">
          <DotLottieVue style="height: 200px; width: 200px" autoplay loop src="/assets/loader.lottie" />
          <h4>{{ t("verifying-payment") }}</h4>
        </div>
      </div>
    </div>
    <div v-else>
      <img v-if="hasError" src="../assets/icons/warn.svg" alt="warn-mark" />
      <img v-else src="../assets/icons/check_mark.svg" alt="check-mark" />
      <h3>{{ getStatusTitle() }}</h3>
      <span>{{ getStatusMessage() }}</span>
      <div v-if="hasError && paymentId" style="margin-top: 16px">
        <span>ID: {{ paymentId }}</span>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

import { DotLottieVue } from "@lottiefiles/dotlottie-vue";
import { useStore } from "vuex";

import authHelper from "@/helpers/authHelper";
import { AnalyticEventsNames } from "@/helpers/google-analytics.helpers";
import { ActionLogTypeConst } from "@/types/IActionLogs";
import { WompiTransactionStatus, type IWompiTransactionResponse } from "@/types/IWompiResponse";

export default defineComponent({
  name: "PaymentCompleted",
  components: { DotLottieVue },
  props: {
    paymentId: {
      type: String,
      required: true
    },
    paymentSuccessRedirect: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const country = computed(() => store.state.payOptions.deviceInfo.country);
    const deviceTag = computed(() => store.state.payOptions.deviceInfo.deviceTag);

    const hasError = ref(false);
    const isLoading = ref(true);
    const transactionResponse = ref<IWompiTransactionResponse | null>(null);

    const getStatusTitle = () => {
      if (props.paymentSuccessRedirect) {
          return t("payment-completed");
      }

      // todo: this switch should be standardized, so is not provider-specific
      switch (transactionResponse.value?.status) {
        case WompiTransactionStatus.PAID:
          return t("payment-completed");
        case WompiTransactionStatus.CREATED:
        case WompiTransactionStatus.PENDING:
        case WompiTransactionStatus.SENT_TO_FE:
        case WompiTransactionStatus.VOIDED:
          return t("payment-pending");
        case WompiTransactionStatus.DECLINED_BY_WOMPI:
        case WompiTransactionStatus.DECLINED_BY_PAYJOY:
        case WompiTransactionStatus.ERROR:
          hasError.value = true;
          return t("payment-failed");
        default:
          hasError.value = true;
          return "Unknown";
      }
    };

    const getStatusMessage = () => {
      if (props.paymentSuccessRedirect) {
          return t("thanks-for-paying");
      }

      // todo: this switch should be standardized, so is not provider-specific
      switch (transactionResponse.value?.status) {
        case WompiTransactionStatus.PAID:
          return t("thanks-for-paying");
        case WompiTransactionStatus.CREATED:
        case WompiTransactionStatus.PENDING:
        case WompiTransactionStatus.SENT_TO_FE:
        case WompiTransactionStatus.VOIDED:
          return t("payment-being-processed");
        case WompiTransactionStatus.DECLINED_BY_WOMPI:
        case WompiTransactionStatus.DECLINED_BY_PAYJOY:
        case WompiTransactionStatus.ERROR:
          hasError.value = true;
          return t("payment-failed-message");
        default:
          hasError.value = true;
          return "Unknown status";
      }
    };

    const checkTransactionStatus = async () => {
      try {
        isLoading.value = true;
        const response = await authHelper.getWompiTransaction(props.paymentId);
        transactionResponse.value = response;
      } catch (error) {
        console.error("Error checking transaction status:", error);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      t,
      store,
      country,
      deviceTag,
      hasError,
      isLoading,
      transactionResponse,
      getStatusTitle,
      getStatusMessage,
      checkTransactionStatus,
    };
  },
  mounted() {
    if(this.paymentSuccessRedirect) {
      this.isLoading = false;
      return;
    }

    this.checkTransactionStatus();

    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnFullyPaidPage,
          actionType: ActionLogTypeConst.PAGE_VIEW,
          data: {
            payment_intention_identifier: this.paymentId,
          },
        },
        this.$gtag
      );
    }
  },
  beforeUnmount() {
    if(this.paymentSuccessRedirect) {
      return;
    }

    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnFullyPaidPage_BrowserClose,
          actionType: ActionLogTypeConst.PAGE_VIEW,
          data: {
            payment_intention_identifier: this.paymentId,
          },
        },
        this.$gtag
      );
    }
  },
});
</script>
<style scoped>
.payments-completed-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.loading-animation-container {
  padding: 16px 22px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.animation-container {
  justify-content: center;
  align-items: center;
  flex: 1;
  display: flex;
}

.animation-container h4 {
  color: var(--Typography-Text-1);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  margin: auto;
  padding-bottom: 80px;
}

.loading-animation {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
}
</style>
